<template>
  <div class="content">
    <div class="meta-section">
      <h3>Details</h3>
      <template v-if="content.type === 'Review'">
        <div class="select-container">
          <label for="brand">Brand</label>
          <select :disabled="!isActive" id="brand" v-model="content.brand">
            <option v-for="brand in brands" v-bind:key="brand.slug" :value="{ id: brand.slug, name: brand.name }">{{ brand.name }}</option>
          </select>
          <!-- prettier-ignore -->
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z" fill="#999"></path></svg>
        </div>
      </template>
      <template v-else-if="content.type === 'SEO'">
        <div class="input-container">
          <label for="keyword">Keyword</label>
          <input :disabled="!isActive" class="input" id="keyword" v-model="content.keyword" />
        </div>
      </template>
      <div class="select-container">
        <label for="site">Site</label>
        <select :disabled="!isActive" id="site" v-model="content.site">
          <option v-for="site in siteList" v-bind:key="site.id" :value="site">{{ site.name }}</option>
        </select>
        <!-- prettier-ignore -->
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z" fill="#999"></path></svg>
      </div>
      <div class="select-container">
        <label for="market">Country</label>
        <select :disabled="!isActive" id="market" v-model="content.language.id">
          <option v-for="market in oldMarketList" v-bind:key="market.id" :value="market.id">{{ market.name }}</option>
        </select>
        <!-- prettier-ignore -->
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z" fill="#999"></path></svg>
      </div>
    </div>
    <div class="seo-section">
      <h3>SEO</h3>
      <div class="input-container" v-if="content.site.id === 'casinobernie'">
        <label for="seo-title">SEO Title ({{ characterCount(content.htmlTitle) }} characters)</label>
        <input :disabled="!isActive" maxlength="65" class="input" autocomplete="off" id="seo-title" type="text" v-model="content.htmlTitle" />
      </div>
      <div class="input-container input-container--full-width">
        <label for="content">SEO Meta Description ({{ characterCount(content.htmlMetaDescription) }} characters)</label>
        <input :disabled="!isActive" maxlength="154" class="input" id="content" v-model="content.htmlMetaDescription" />
      </div>
    </div>
    <div class="content-section">
      <h3>Content</h3>
      <div class="input-container" v-if="content.type === 'SEO'">
        <label for="title">Title ({{ wordCount(content.title) }} words)</label>
        <input :disabled="!isActive" class="input" autocomplete="off" id="title" type="text" v-model="content.title" />
      </div>
      <div v-if="content.type === 'Review'" class="textarea-container">
        <label for="summary">Review summary ({{ wordCount(content.summary) }} words)</label>
        <textarea :disabled="!isActive" class="textarea" id="summary" rows="6" v-model="content.summary"></textarea>
      </div>
      <div class="textarea-container">
        <label v-if="content.type === 'Review'" for="content" data-cy="content-words">Review ({{ wordCount(content.content) }} words)</label>
        <label v-else for="content" data-cy="content-words">Content ({{ wordCount(content.content) }} words)</label>
        <textarea :disabled="!isActive" class="textarea" id="content" rows="20" v-model="content.content"></textarea>
      </div>
      <div v-if="content.type === 'Review' && content.site.id != 'casinobernie'" class="textarea-container">
        <label for="bonus-review">Bonus review ({{ wordCount(content.bonusReview) }} words)</label>
        <textarea :disabled="!isActive" class="textarea" id="bonus-review" rows="6" v-model="content.bonusReview" data-cy="bonus-review"></textarea>
      </div>
      <div v-if="content.type === 'Review' && content.site.id != 'casinobernie'" class="textarea-container">
        <label for="game-review">Game selection review ({{ wordCount(content.gameSelectionReview) }} words)</label>
        <textarea :disabled="!isActive" class="textarea" id="game-review" rows="6" v-model="content.gameSelectionReview" data-cy="game-review"></textarea>
      </div>
    </div>
    <div class="pros-section" v-if="content.type === 'Review' && content.site.id != 'casinobernie'">
      <div v-for="(pro, index) in content.pros" v-bind:key="`pros${index}`">
        <h3>Pros</h3>
        <div class="input-container">
          <label for="title">{{ index + 1 }}</label>
          <input :disabled="!isActive" class="input" autocomplete="off" v-model="content.pros[index]" :data-cy="`pro-${index + 1}`" />
        </div>
      </div>
    </div>
    <div class="cons-section" v-if="content.type === 'Review' && content.site.id != 'casinobernie'">
      <div v-for="(con, index) in content.cons" v-bind:key="`cons${index}`">
        <h3>Cons</h3>
        <div class="input-container">
          <label for="title">{{ index + 1 }}</label>
          <input :disabled="!isActive" class="input" autocomplete="off" v-model="content.cons[index]" :data-cy="`con-${index + 1}`" />
        </div>
      </div>
    </div>
    <div class="faq-section" v-if="content.faq">
      <h3>FAQ</h3>
      <div class="input-container input-container--full-width">
        <label>Title</label>
        <input :disabled="!isActive" class="input" autocomplete="off" v-model="content.faq.title" data-cy="faq-title" />
      </div>
      <div>
        <div class="question" v-for="(question, index) in content.faq.questions" v-bind:key="`question${index}`">
          <div class="input-container input-container--full-width" v-if="question">
            <label>Question {{ index + 1 }}</label>
            <input :disabled="!isActive" class="input" autocomplete="off" v-model="question.question" :data-cy="`question-${index + 1}`" />
          </div>
          <div class="input-container input-container--full-width" v-if="question">
            <label>Answer {{ index + 1 }}</label>
            <input :disabled="!isActive" class="input" autocomplete="off" v-model="question.answer" :data-cy="`answer-${index + 1}`" />
          </div>
          <div v-if="isActive">
            <div class="circle circle--open" v-on:click="newQuestion()">
              <div class="circle__before"></div>
              <div class="circle__after"></div>
            </div>
            <div v-if="index !== 0" class="circle circle--close" v-on:click="deleteQuestion(index)">
              <div class="circle__before"></div>
              <div class="circle__after"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="how-to-section" v-if="content.type === 'SEO' && content.howTo">
      <h3>How To</h3>
      <div class="input-container input-container--full-width">
        <label>Headline</label>
        <input :disabled="!isActive" class="input" autocomplete="off" v-model="content.howTo.headline" data-cy="howto-headline" />
      </div>
      <div class="textarea-container">
        <label>Description</label>
        <textarea :disabled="!isActive" class="textarea" rows="6" v-model="content.howTo.description" data-cy="howto-description"></textarea>
      </div>
      <div class="input-container">
        <label>Time</label>
        <vue-timepicker hide-clear-button v-model="content.howTo.time" data-cy="howto-time"></vue-timepicker>
      </div>
      <div class="tool" v-for="(tool, index) in content.howTo.tools" v-bind:key="`tool-${index}`">
        <div class="input-container">
          <label>Tool {{ index + 1 }}</label>
          <input :disabled="!isActive" class="input" autocomplete="off" v-model="tool.item" data-cy="howto-tool" />
        </div>
        <div v-if="isActive">
          <div class="circle circle--open" v-on:click="newTool()">
            <div class="circle__before"></div>
            <div class="circle__after"></div>
          </div>
          <div v-if="index !== 0" class="circle circle--close" v-on:click="deleteTool(index)">
            <div class="circle__before"></div>
            <div class="circle__after"></div>
          </div>
        </div>
      </div>
      <div class="supply" v-for="(supply, index) in content.howTo.supplies" v-bind:key="`supply-${index}`">
        <div class="input-container">
          <label>Supply {{ index + 1 }}</label>
          <input :disabled="!isActive" class="input" autocomplete="off" v-model="supply.item" :data-cy="`supply-${index + 1}`" />
        </div>
        <div v-if="isActive">
          <div class="circle circle--open" v-on:click="newSupply()" data-cy="new-supply-button">
            <div class="circle__before"></div>
            <div class="circle__after"></div>
          </div>
          <div v-if="index !== 0" class="circle circle--close" v-on:click="deleteSupply(index)">
            <div class="circle__before"></div>
            <div class="circle__after"></div>
          </div>
        </div>
      </div>
      <div class="step" v-for="(step, index) in content.howTo.steps" v-bind:key="`step-${index}`">
        <span>Step {{ index + 1 }}</span>
        <div class="input-container">
          <label>Headline</label>
          <input :disabled="!isActive" class="input" autocomplete="off" v-model="step.headline" :data-cy="`step-headline-${index + 1}`" />
        </div>
        <!-- <div class="input-container">
          <label>Name</label>
          <input :disabled="!isActive" class="input" autocomplete="off" v-model="step.name" :data-cy="`step-name-${index + 1}`" />
        </div> -->
        <div class="textarea-container">
          <label>Description</label>
          <textarea :disabled="!isActive" class="textarea" rows="6" v-model="step.description" :data-cy="`step-description-${index + 1}`"></textarea>
        </div>
        <div v-if="isActive">
          <div class="circle circle--open" v-on:click="newStep()">
            <div class="circle__before"></div>
            <div class="circle__after"></div>
          </div>
          <div v-if="index !== 0" class="circle circle--close" v-on:click="deleteStep(index)">
            <div class="circle__before"></div>
            <div class="circle__after"></div>
          </div>
        </div>
      </div>
    </div>
    <div class="pros-cons-section" v-if="content.type === 'SEO' && content.prosConsShortcode">
      <h3>Pros and cons shortcode</h3>
      <div class="input-container input-container--full-width">
        <label>Headline</label>
        <input :disabled="!isActive" class="input" autocomplete="off" v-model="content.prosConsShortcode.headline" data-cy="short-headline" />
      </div>
      <div class="textarea-container">
        <label>Description</label>
        <textarea :disabled="!isActive" class="textarea" rows="6" v-model="content.prosConsShortcode.description" data-cy="short-description"></textarea>
      </div>
      <div class="pros" v-for="(pro, index) in content.prosConsShortcode.pros" v-bind:key="`pro-${index}`">
        <span>Pro {{ index + 1 }}</span>
        <div class="input-container input-container--full-width">
          <input :disabled="!isActive" class="input" autocomplete="off" v-model="content.prosConsShortcode.pros[index].item" :data-cy="`pro-short-${index + 1}`" />
        </div>
        <div v-if="isActive">
          <div class="circle circle--open" v-on:click="newPro()">
            <div class="circle__before"></div>
            <div class="circle__after"></div>
          </div>
          <div v-if="index !== 0" class="circle circle--close" v-on:click="deletePro(index)">
            <div class="circle__before"></div>
            <div class="circle__after"></div>
          </div>
        </div>
      </div>
      <div class="cons" v-for="(con, index) in content.prosConsShortcode.cons" v-bind:key="`con-${index}`">
        <span>Con {{ index + 1 }}</span>
        <div class="input-container input-container--full-width">
          <input :disabled="!isActive" class="input" autocomplete="off" v-model="content.prosConsShortcode.cons[index].item" :data-cy="`con-short-${index + 1}`" />
        </div>
        <div v-if="isActive">
          <div class="circle circle--open" v-on:click="newCon()">
            <div class="circle__before"></div>
            <div class="circle__after"></div>
          </div>
          <div v-if="index !== 0" class="circle circle--close" v-on:click="deleteCon(index)">
            <div class="circle__before"></div>
            <div class="circle__after"></div>
          </div>
        </div>
      </div>
    </div>
    <div class="wordcount">
      <KeywordCounter :content="content"></KeywordCounter>
      <span data-cy="total-words">Total word count: {{ contentWordCount(content) }}</span>
    </div>
  </div>
</template>

<script>
import sharedWordCountScript from '@/assets/shared-scripts/wordcount'
import KeywordCounter from '@/components/keyword-counter'
import VueTimepicker from 'vue2-timepicker'
import 'vue2-timepicker/dist/VueTimepicker.css'

export default {
  created() {
    this.wordCount = sharedWordCountScript.wordCounter
    this.contentWordCount = sharedWordCountScript.contentWordCount
  },
  props: {
    content: Object,
  },
  methods: {
    newQuestion() {
      const newQuestion = { question: '', answer: '' }
      this.content.faq.questions.push(newQuestion)
    },
    deleteQuestion(index) {
      this.content.faq.questions.splice(index, 1)
    },
    newTool() {
      const newTool = { item: '' }
      this.content.howTo.tools.push(newTool)
    },
    deleteTool(index) {
      this.content.howTo.tools.splice(index, 1)
    },
    newSupply() {
      const newSupply = { item: '' }
      this.content.howTo.supplies.push(newSupply)
    },
    deleteSupply(index) {
      this.content.howTo.supplies.splice(index, 1)
    },
    newStep() {
      const newStep = { headline: '', name: '', description: '' }
      this.content.howTo.steps.push(newStep)
    },
    deleteStep(index) {
      this.content.howTo.steps.splice(index, 1)
    },
    newPro() {
      const newPro = { item: '' }
      this.content.prosConsShortcode.pros.push(newPro)
    },
    deletePro(index) {
      this.content.prosConsShortcode.pros.splice(index, 1)
    },
    newCon() {
      const newCon = { item: '' }
      this.content.prosConsShortcode.cons.push(newCon)
    },
    deleteCon(index) {
      this.content.prosConsShortcode.cons.splice(index, 1)
    },
    characterCount(string) {
      return string.length
    },
  },
  computed: {
    oldMarketList() {
      return this.$store.getters.oldMarketList
    },
    siteList() {
      return this.$store.getters.siteList
    },
    brands() {
      return this.$store.getters.brands
    },
    isActive() {
      return this.content.status == 'Draft' || this.content.status == 'Revision Needed' ? true : false
    },
  },
  components: {
    KeywordCounter,
    VueTimepicker,
  },
}
</script>

<style lang="scss" scoped>
@import '../assets/shared-styles/input.scss';
@import '../assets/shared-styles/textarea.scss';
@import '../assets/shared-styles/select.scss';

.content {
  background: white;
}
.meta-section,
.seo-section,
.content-section,
.pros-section,
.cons-section,
.faq-section,
.wordcount,
.how-to-section,
.pros-cons-section {
  position: relative;
  display: grid;
  gap: 1rem 1.5rem;
  padding: 1.5rem;
  h3 {
    font-size: 1rem;
    grid-column: 1 / -1;
    font-weight: 400;
    margin: 0;
  }
  &:before {
    content: '';
    position: absolute;
    top: calc(100%);
    width: 100%;
    height: 1px;
    background: #ebebf2;
  }
}
.wordcount {
  grid-template-columns: 1fr auto;
  align-items: center;
  span {
    font-size: 0.875rem;
  }
}
.meta-section {
  grid-template-columns: auto auto 1fr;
}
.seo-section {
  grid-template-columns: auto 1fr;
}
.content-section {
  grid-template-columns: auto;
}
.pros-section,
.cons-section {
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
}
.question,
.step,
.pros,
.cons,
.tool,
.supply {
  position: relative;
  display: grid;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
  padding: 1.5rem;
  background-color: #ebebf0;
  border-radius: 0.25rem;
  .input {
    background: white;
  }
  span {
    font-size: 0.8125rem;
  }
}

.tool .input-container,
.supply .input-container,
.step .input-container {
  display: grid;
  grid-template-columns: 60px 1fr;
  align-items: center;
  gap: 0.25rem;
}

.pros,
.cons {
  grid-template-columns: auto 1fr;
  align-items: center;
  padding: 0.75rem 3rem 0.75rem 0.75rem;
}

.circle {
  position: absolute;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background-color: var(--color-cta);
  cursor: pointer;
  z-index: 2;
}
.circle--open {
  bottom: 0;
  left: 50%;
  transform: translate(-50%, 50%);
}
.circle__before,
.circle__after {
  content: '';
  position: absolute;
  width: 16px;
  height: 1px;
  background-color: white;
  border-radius: 0;
  top: 13px;
  z-index: 0;
}
.circle--close > .circle__before,
.circle--close > .circle__after {
  background-color: #43425d;
}
.circle--close:hover > .circle__before,
.circle--close:hover > .circle__after {
  height: 2px;
  background-color: var(--color-cta);
}
.circle__before {
  left: 5px;
}
.circle__after {
  -webkit-transform: rotate(-90deg);
  -moz-transform: rotate(-90deg);
  transform: rotate(-90deg);
  right: 5px;
}
.circle--close {
  background-color: transparent;
  right: 0;
  top: 0;
  transform: translate(-50%, 50%);
}
.circle--close .circle__before {
  transform: rotate(225deg);
}
.circle--close .circle__after {
  transform: rotate(-225deg);
}
.circle--centered {
  top: 5px;
}
</style>
